import React from 'react';
import { Alert, AlertIcon, Box, Text, Link, VStack, HStack } from '@chakra-ui/react';
import { HiPhone, HiMail } from 'react-icons/hi';
import { FaWhatsapp } from 'react-icons/fa';
import { COLORS } from '../styles/colors';
import { getTenantBranding } from '../utils/tenantBranding';

export const isUnderMinimumRegistrationAge = (birthAge) => {
    if (birthAge === null || birthAge === undefined || birthAge === '') return false;
    return Number(birthAge) < 18;
};

export const buildAgeEligibilityMessage = (courseDetails, birthAge) => {
    const age = birthAge !== null && birthAge !== undefined && birthAge !== '' ? Number(birthAge) : null;
    const agePhrase = age !== null && !Number.isNaN(age)
        ? `but I am under 18 years old (I am ${age} now)`
        : 'but I am under 18 years old';
    const shortName = getTenantBranding().shortName;
    const adminLabel = shortName ? `${shortName} Admin` : 'Admin';

    let message = `Hi ${adminLabel},\n\nI am trying to register for a course, ${agePhrase} and received the age eligibility notice on the registration form.`;

    if (courseDetails?.courseName) {
        message += `\n\nCourse: ${courseDetails.courseName}`;
        if (courseDetails.batchName) {
            message += `\nBatch: ${courseDetails.batchName}`;
        }
        if (courseDetails.startDate && courseDetails.endDate) {
            message += `\nSchedule: ${courseDetails.startDate} – ${courseDetails.endDate}`;
        }
    }

    message += '\n\nCould you please advise me on the next steps?\n\nThank you.';
    return message;
};

const AgeEligibilityAlert = ({ courseDetails, birthAge }) => {
    const branding = getTenantBranding();
    const adminPhone = branding.contactPhone || branding.phone || '';
    const adminEmail = branding.email || '';
    const message = buildAgeEligibilityMessage(courseDetails, birthAge);
    const whatsappDigits = (branding.whatsapp || adminPhone).replace(/[^0-9]/g, '');
    const whatsappUrl = whatsappDigits
        ? `https://wa.me/${whatsappDigits}?text=${encodeURIComponent(message)}`
        : null;
    const emailUrl = adminEmail
        ? `mailto:${adminEmail}?subject=${encodeURIComponent('Age Eligibility Enquiry - Course Registration')}&body=${encodeURIComponent(message)}`
        : null;

    return (
        <Alert
            status="warning"
            variant="left-accent"
            borderRadius="md"
            mt={3}
            bg="orange.50"
            borderColor={COLORS.mandarin}
        >
            <AlertIcon color={COLORS.mandarin} />
            <Box>
                <Text fontWeight="semibold" fontSize="sm" color={COLORS.maastrichtBlue}>
                    Age eligibility not met. Please contact our Admin Department.
                </Text>
                <VStack align="start" spacing={1.5} mt={2}>
                    {adminPhone && (
                        <HStack spacing={2}>
                            <Box as={HiPhone} color={COLORS.shadowBlue} boxSize={4} />
                            <Text fontSize="sm" color={COLORS.shadowBlue}>
                                Phone:{' '}
                                <Link href={`tel:${adminPhone.replace(/\s/g, '')}`} color={COLORS.mandarin} fontWeight="medium" _hover={{ textDecoration: 'underline' }}>
                                    {adminPhone}
                                </Link>
                            </Text>
                            {whatsappUrl && (
                                <Link
                                    href={whatsappUrl}
                                    isExternal
                                    aria-label="Contact Admin via WhatsApp about age eligibility"
                                    title="Chat on WhatsApp"
                                    display="inline-flex"
                                    alignItems="center"
                                    color="#25D366"
                                    _hover={{ color: '#128C7E', transform: 'scale(1.05)' }}
                                    transition="all 0.2s"
                                >
                                    <Box as={FaWhatsapp} boxSize={5} />
                                </Link>
                            )}
                        </HStack>
                    )}
                    {adminEmail && (
                        <HStack spacing={2}>
                            <Box as={HiMail} color={COLORS.shadowBlue} boxSize={4} />
                            <Text fontSize="sm" color={COLORS.shadowBlue}>
                                Email:{' '}
                                <Link href={emailUrl} color={COLORS.mandarin} fontWeight="medium" _hover={{ textDecoration: 'underline' }}>
                                    {adminEmail}
                                </Link>
                            </Text>
                        </HStack>
                    )}
                </VStack>
            </Box>
        </Alert>
    );
};

export default AgeEligibilityAlert;
